import {
  McpCalendarReadScope,
  McpConsentQueryParam,
  McpGraphReadScope,
  McpGraphWriteScope,
  type SaleskoOAuthProvider
} from "@salesko/contracts";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { McpConsentSurface, type McpConsentScopeKey } from "../components/McpConsentSurface";
import { LOCALE_HTML_LANG, useI18n } from "../i18n";
import { authProviderMode, issueMcpConsentGrant, validateMcpAuthorizeReturnTo } from "../lib/api";
import { useAgentSession } from "../workspace/hooks/useAgentSession";

type McpConsentSearch = {
  return_to?: string;
};

// Where the sign-in click handler stashes `return_to` before leaving for the
// OAuth provider, and where this route reads it back from if it remounts
// without the query param still attached.
const mcpConsentReturnToStorageKey = "salesko.mcpConsent.returnTo";

export const Route = createFileRoute("/mcp-consent")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): McpConsentSearch => {
    const returnTo = typeof search.return_to === "string" && search.return_to.length > 0 ? search.return_to : undefined;
    return returnTo ? { return_to: returnTo } : {};
  },
  head: () => ({
    meta: [
      { title: "Authorize MCP access - Salesko" },
      {
        name: "description",
        content: "Review and grant an MCP client access to your Salesko graph."
      },
      { name: "robots", content: "noindex, nofollow" }
    ]
  }),
  component: McpConsentRoute
});

function McpConsentRoute() {
  const { return_to } = Route.useSearch();
  const { locale } = useI18n();
  const { availableAuthProviders, handleAuthProviderSignIn, storedSession } = useAgentSession();
  const [returnToUrl] = useState<URL | null>(() => resolveReturnTo(return_to));
  const [grantGraphRead, setGrantGraphRead] = useState(true);
  const [grantCalendarRead, setGrantCalendarRead] = useState(true);
  const [grantGraphWrite, setGrantGraphWrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [issuanceFailed, setIssuanceFailed] = useState(false);

  function handleSignIn(provider: SaleskoOAuthProvider) {
    if (return_to) {
      try {
        window.sessionStorage.setItem(mcpConsentReturnToStorageKey, return_to);
      } catch {
        // Best-effort; sessionStorage may be unavailable (private browsing).
      }
    }
    void handleAuthProviderSignIn(provider);
  }

  function handleToggleScope(scope: McpConsentScopeKey) {
    if (scope === "graphRead") {
      setGrantGraphRead((value) => !value);
    } else if (scope === "calendarRead") {
      setGrantCalendarRead((value) => !value);
    } else {
      setGrantGraphWrite((value) => !value);
    }
  }

  async function handleContinue() {
    if (!returnToUrl || !storedSession) {
      return;
    }

    const scopes = [
      ...(grantGraphRead ? [McpGraphReadScope] : []),
      ...(grantCalendarRead ? [McpCalendarReadScope] : []),
      ...(grantGraphWrite ? [McpGraphWriteScope] : [])
    ];

    setSubmitting(true);
    setIssuanceFailed(false);
    try {
      const grant = await issueMcpConsentGrant({ scopes, ttlSeconds: 600 }, storedSession.token);
      const target = new URL(returnToUrl.href);
      target.searchParams.set(McpConsentQueryParam, grant.token);
      window.location.assign(target.toString());
    } catch {
      setIssuanceFailed(true);
      setSubmitting(false);
    }
  }

  if (!returnToUrl) {
    return (
      <main className="mcp-consent-shell" lang={LOCALE_HTML_LANG[locale]}>
        <McpConsentSurface status="invalid" />
      </main>
    );
  }

  if (!storedSession) {
    if (authProviderMode === "none") {
      return (
        <main className="mcp-consent-shell" lang={LOCALE_HTML_LANG[locale]}>
          <McpConsentSurface status="authNotConfigured" />
        </main>
      );
    }

    return (
      <main className="mcp-consent-shell" lang={LOCALE_HTML_LANG[locale]}>
        <McpConsentSurface availableAuthProviders={availableAuthProviders} onSignIn={handleSignIn} status="signIn" />
      </main>
    );
  }

  return (
    <main className="mcp-consent-shell" lang={LOCALE_HTML_LANG[locale]}>
      <McpConsentSurface
        issuanceFailed={issuanceFailed}
        onContinue={() => void handleContinue()}
        onToggleScope={handleToggleScope}
        scopes={{ calendarRead: grantCalendarRead, graphRead: grantGraphRead, graphWrite: grantGraphWrite }}
        session={{
          ...(storedSession.email ? { email: storedSession.email } : {}),
          ...(storedSession.name ? { name: storedSession.name } : {}),
          tenantId: storedSession.tenantId
        }}
        status="form"
        submitting={submitting}
      />
    </main>
  );
}

function resolveReturnTo(searchReturnTo: string | undefined): URL | null {
  if (searchReturnTo) {
    return validateMcpAuthorizeReturnTo(searchReturnTo);
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    return validateMcpAuthorizeReturnTo(window.sessionStorage.getItem(mcpConsentReturnToStorageKey));
  } catch {
    return null;
  }
}
